import { Armour, BaseResource, BaseType, Creature, Spell, TreasureItem, Weapon } from '../types';
import { formatSoldierType } from '../utils/formatting';
import { matchesIndexQuery } from './indexSearch';

export type FrostgraveIndexCategory = 'spells' | 'bestiary' | 'equipment' | 'items' | 'bases';

export interface FrostgraveReferenceEntry {
  category: 'equipment' | 'items' | 'bases';
  id: string;
  group?: string;
  /** Visual treatment for magic items: scrolls/grimoires, potions, or other magic. */
  kind?: 'scroll' | 'potion' | 'magic';
  name: string;
  description: string | null;
  modifiers?: string;
  searchText: string;
}

export type FrostgraveIndexEntry =
  | { category: 'spells'; id: string; spell: Spell; searchText: string }
  | { category: 'bestiary'; id: string; creature: Creature; searchText: string }
  | FrostgraveReferenceEntry;

export interface FrostgraveIndexSources {
  spells: Spell[];
  creatures: Creature[];
  weapons?: Weapon[];
  armours?: Armour[];
  treasureItems?: TreasureItem[];
  baseTypes?: BaseType[];
  baseResources?: BaseResource[];
}

export const getCreatureSearchText = (creature: Creature): string => [
  creature.name,
  formatSoldierType(creature.type),
  creature.notes,
  ...creature.traits.flatMap((trait) => [trait.name, trait.description]),
  ...creature.weapons.map((item) => item.name),
  ...creature.armourItems.map((item) => item.name),
  ...creature.treasureItems.map((item) => item.name),
].filter(Boolean).join(' ').toLowerCase();

const byName = <T extends { name: string }>(items: T[]): T[] =>
  [...items].sort((left, right) => left.name.localeCompare(right.name));

const referenceEntry = (
  category: FrostgraveReferenceEntry['category'],
  id: string,
  name: string,
  description: string | null,
  modifiers: Array<string | null>,
  group?: string
): FrostgraveReferenceEntry => {
  const modifierText = modifiers.filter(Boolean).join(' | ');
  return {
    category, id, group, name, description,
    modifiers: modifierText || undefined,
    searchText: `${name} ${group ?? ''} ${description ?? ''} ${modifierText}`.toLowerCase(),
  };
};

const isGrimoire = (item: TreasureItem) => item.id.toLowerCase().startsWith('grimoire_');
const isScroll = (item: TreasureItem) => item.id.toLowerCase().startsWith('scroll_');

export const getMagicItemKind = (item: TreasureItem): 'scroll' | 'potion' | 'magic' =>
  isScroll(item) || isGrimoire(item)
    ? 'scroll'
    : item.baseItemType === 'potion'
      ? 'potion'
      : 'magic';

export const getMagicItemGroup = (item: TreasureItem): string => {
  if (isScroll(item)) return 'Scrolls';
  if (isGrimoire(item)) return 'Grimoires';
  if (item.baseItemType === 'potion') return 'Potions';
  if (item.baseItemType === 'weapon') return 'Weapons';
  if (item.baseItemType === 'armour') return 'Armour';
  return 'Items';
};

const signed = (value: number) => `${value >= 0 ? '+' : ''}${value}`;

export function buildFrostgraveIndexEntries(sources: FrostgraveIndexSources): FrostgraveIndexEntry[] {
  const { spells, creatures, weapons = [], armours = [], treasureItems = [], baseTypes = [], baseResources = [] } = sources;
  const sortedSpells = [...spells].sort((left, right) =>
    left.school.localeCompare(right.school) || left.name.localeCompare(right.name)
  );
  const sortedCreatures = [...creatures].sort((left, right) =>
    formatSoldierType(left.type, true).localeCompare(formatSoldierType(right.type, true))
    || left.name.localeCompare(right.name)
  );
  const equipmentEntries = [
    ...weapons.map((weapon) => referenceEntry('equipment', weapon.id, weapon.name, weapon.description, [
      weapon.damageModifier !== null ? `Damage ${signed(weapon.damageModifier)}` : null,
      weapon.maxRange !== null ? `Range ${weapon.maxRange}"` : null,
      weapon.slotsConsumed !== null ? `${weapon.slotsConsumed} slot${weapon.slotsConsumed === 1 ? '' : 's'}` : null,
      ...weapon.specialTags.map((tag) => tag.id),
    ], 'Weapons')),
    ...armours.map((armour) => referenceEntry('equipment', armour.id, armour.name, armour.description, [
      armour.armourModifier !== null ? `Armour ${signed(armour.armourModifier)}` : null,
      ...armour.specialTags.map((tag) => tag.id),
    ], 'Armour')),
  ].sort((left, right) => (left.group ?? '').localeCompare(right.group ?? '') || left.name.localeCompare(right.name));
  return [
    ...sortedSpells.map((spell): FrostgraveIndexEntry => ({
      category: 'spells', id: spell.id, spell,
      searchText: `${spell.name} ${spell.school} ${spell.description ?? ''}`.toLowerCase(),
    })),
    ...sortedCreatures.map((creature): FrostgraveIndexEntry => ({
      category: 'bestiary', id: creature.id, creature,
      searchText: getCreatureSearchText(creature),
    })),
    ...equipmentEntries,
    ...[...treasureItems]
      .sort((left, right) =>
        getMagicItemGroup(left).localeCompare(getMagicItemGroup(right))
        || left.name.localeCompare(right.name))
      .map((item) => ({
        ...referenceEntry('items', item.id, item.name, item.description, [
          item.purchasePrice !== null ? `Buy ${item.purchasePrice}gc` : null,
          `Sell ${item.salePrice}gc`,
          item.usesPerGame > 0 ? `${item.usesPerGame} use${item.usesPerGame === 1 ? '' : 's'}/game${item.refreshAfterGame ? ', refreshes' : ''}` : null,
        ], getMagicItemGroup(item)),
        kind: getMagicItemKind(item),
      })),
    ...byName(baseTypes).map((base) => referenceEntry('bases', base.id, base.name, base.description, [], 'Base Types')),
    ...byName(baseResources).map((resource) => referenceEntry('bases', resource.id, resource.name, resource.description, [
      resource.cost !== null ? `${resource.cost}gc` : null,
    ], 'Base Upgrades')),
  ];
}

export function filterFrostgraveIndex(
  entries: FrostgraveIndexEntry[],
  query: string,
  category: FrostgraveIndexCategory | 'all' = 'all'
): FrostgraveIndexEntry[] {
  return entries.filter((entry) =>
    (category === 'all' || entry.category === category)
    && matchesIndexQuery(entry.searchText, query)
  );
}
